import React, { useState, useEffect } from 'react';
import { FaPlus, FaEdit, FaTrash, FaSearch, FaCheck, FaTimes } from 'react-icons/fa';

const Fejlmeldinger = () => {
  const [fejlmeldinger, setFejlmeldinger] = useState([]);
  const [biler, setBiler] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingFejlmelding, setEditingFejlmelding] = useState(null);
  const [formData, setFormData] = useState({
    bil_id: '',
    internt_nummer: '',
    beskrivelse: '',
    kategori: 'Fejlmelding',
    status: 'Åben'
  });
  const [bilSearchTerm, setBilSearchTerm] = useState('');
  const [showBilDropdown, setShowBilDropdown] = useState(false);
  const [selectedBil, setSelectedBil] = useState(null);

  const resetForm = () => {
    setFormData({
      bil_id: '',
      internt_nummer: '',
      beskrivelse: '',
      kategori: 'Fejlmelding',
      status: 'Åben'
    });
    setBilSearchTerm('');
    setSelectedBil(null);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingFejlmelding(null);
    resetForm();
  };

  useEffect(() => {
    fetchFejlmeldinger();
    fetchBiler();
  }, []);

  const fetchFejlmeldinger = async () => {
    try {
      const response = await fetch('/api/fejlmeldinger');
      const data = await response.json();
      setFejlmeldinger(data);
    } catch (error) {
      console.error('Error fetching fejlmeldinger:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchBiler = async () => {
    try {
      const response = await fetch('/api/biler');
      const data = await response.json();
      setBiler(data);
    } catch (error) {
      console.error('Error fetching biler:', error);
    }
  };

  // Filtrer biler baseret på søgeterm
  const filteredBiler = biler.filter(bil =>
    bil.internt_nummer.toLowerCase().includes(bilSearchTerm.toLowerCase()) ||
    (bil.station_navn && bil.station_navn.toLowerCase().includes(bilSearchTerm.toLowerCase()))
  );

  const handleBilSearchChange = (e) => {
    const value = e.target.value;
    setBilSearchTerm(value);
    
    // Hvis der er en valgt bil og værdien matcher, brug bil_id, ellers brug værdien som manuel indtastning
    if (selectedBil && value === `${selectedBil.internt_nummer} - ${selectedBil.station_navn || 'Ingen station'}`) {
      setFormData({ ...formData, bil_id: selectedBil.id, internt_nummer: '' });
    } else {
      setFormData({ ...formData, bil_id: '', internt_nummer: value });
      setSelectedBil(null);
    }
    
    setShowBilDropdown(value.length > 0);
  };

  const handleBilSelect = (bil) => {
    setSelectedBil(bil);
    setBilSearchTerm(`${bil.internt_nummer} - ${bil.station_navn || 'Ingen station'}`);
    setFormData({ ...formData, bil_id: bil.id, internt_nummer: '' });
    setShowBilDropdown(false);
  };

  const handleBilInputBlur = () => {
    // Vent lidt før vi skjuler dropdown for at tillade klik
    setTimeout(() => setShowBilDropdown(false), 200);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const url = editingFejlmelding ? `/api/fejlmeldinger/${editingFejlmelding.id}` : '/api/fejlmeldinger';
      const method = editingFejlmelding ? 'PUT' : 'POST';
      const manualBilValue = bilSearchTerm.trim();
      const payload = {
        ...formData,
        bil_id: selectedBil ? selectedBil.id : '',
        internt_nummer: selectedBil ? '' : manualBilValue
      };

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

        if (response.ok) {
          closeModal();
        fetchFejlmeldinger();
      }
    } catch (error) {
      console.error('Error saving fejlmelding:', error);
    }
  };

  const handleEdit = (fejlmelding) => {
    setEditingFejlmelding(fejlmelding);
    setFormData(fejlmelding);
    
    // Hvis der er en bil tilknyttet, find bilen og sæt søgetermen
    if (fejlmelding.bil_id && fejlmelding.bil_internt_nummer) {
      const bil = biler.find(b => b.id === fejlmelding.bil_id);
      if (bil) {
        setSelectedBil(bil);
        setBilSearchTerm(`${bil.internt_nummer} - ${bil.station_navn || 'Ingen station'}`);
      } else {
        setBilSearchTerm(fejlmelding.bil_internt_nummer);
      }
    } else {
      setBilSearchTerm('');
      setSelectedBil(null);
    }
    
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Er du sikker på, at du vil slette denne fejlmelding?')) {
      try {
        const response = await fetch(`/api/fejlmeldinger/${id}`, {
          method: 'DELETE',
        });

        if (response.ok) {
          fetchFejlmeldinger();
        }
      } catch (error) {
        console.error('Error deleting fejlmelding:', error);
      }
    }
  };

  const handleMarkAsUdfort = async (id) => {
    if (window.confirm('Er du sikker på, at du vil markere denne fejlmelding som udført?')) {
      try {
        const response = await fetch(`/api/fejlmeldinger/${id}/udfort`, {
          method: 'PATCH',
        });

        if (response.ok) {
          fetchFejlmeldinger();
        }
      } catch (error) {
        console.error('Error marking fejlmelding as udfort:', error);
      }
    }
  };

  const filteredFejlmeldinger = fejlmeldinger.filter(fejlmelding =>
    fejlmelding.beskrivelse.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (fejlmelding.bil_internt_nummer && fejlmelding.bil_internt_nummer.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (fejlmelding.station_navn && fejlmelding.station_navn.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const getPrioritetColor = (prioritet) => {
    switch (prioritet) {
      case 'Høj':
        return 'bg-red-100 text-red-800';
      case 'Medium':
        return 'bg-yellow-100 text-yellow-800';
      case 'Lav':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Åben':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      case 'I gang':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'Udført':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'Løst':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-falck-blue"></div>
      </div>
    );
  }

  return (
    <div className="px-2 sm:px-4 md:px-6 py-2 sm:py-4">
             <div className="flex justify-between items-center mb-6">
         <div>
           <h1 className="text-2xl font-bold text-gray-800">Fejlmeldinger</h1>
           <p className="text-sm text-gray-600 mt-1">Sorteret: Åbne → I gang → Udførte</p>
         </div>
          <button
            onClick={() => {
              setEditingFejlmelding(null);
              resetForm();
              setShowModal(true);
            }}
            className="bg-falck-blue text-white px-4 py-2 rounded-lg hover:bg-falck-dark-blue transition-colors flex items-center"
          >
            <FaPlus className="mr-2" />
            Tilføj fejlmelding
          </button>
       </div>

      {/* Søgefelt */}
      <div className="mb-6">
        <div className="relative">
          <input
            type="text"
            placeholder="Søg efter fejlmeldinger..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-falck-blue bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
          />
          <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
        </div>
      </div>

      {/* Fejlmeldinger tabel */}
      <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg shadow overflow-hidden">
        <div className="scroll-fade-x">
          <div className="scroll-area">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-gray-900 dark:text-white">
          <thead className="bg-gray-50 dark:bg-gray-800">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Beskrivelse
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Bil
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Station
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Kategori
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Dato
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Handlinger
              </th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
            {filteredFejlmeldinger.map((fejlmelding) => (
              <tr key={fejlmelding.id} className="hover:bg-gray-50">
                <td className="px-6 py-4">
                  <div className="text-sm font-medium text-gray-900">
                    {fejlmelding.beskrivelse}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">
                    {fejlmelding.bil_internt_nummer || 'Ikke angivet'}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">
                    {fejlmelding.station_navn || 'Ikke tildelt'}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                    {fejlmelding.kategori}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(fejlmelding.status)}`}>
                    {fejlmelding.status}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">
                    {fejlmelding.oprettet_dato ? new Date(fejlmelding.oprettet_dato).toLocaleDateString('da-DK') : 'Ikke angivet'}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <button
                    onClick={() => handleEdit(fejlmelding)}
                    className="text-falck-blue hover:text-falck-dark-blue mr-3"
                    title="Rediger"
                  >
                    <FaEdit className="h-4 w-4" />
                  </button>
                  {fejlmelding.status !== 'Udført' && (
                    <button
                      onClick={() => handleMarkAsUdfort(fejlmelding.id)}
                      className="text-green-600 hover:text-green-800 mr-3"
                      title="Markér som udført"
                    >
                      <FaCheck className="h-4 w-4" />
                    </button>
                  )}
                  <button
                    onClick={() => handleDelete(fejlmelding.id)}
                    className="text-red-600 hover:text-red-800"
                    title="Slet"
                  >
                    <FaTrash className="h-4 w-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center px-4 py-6 sm:py-10 overflow-y-auto">
            <div
              className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm"
              aria-hidden="true"
              onClick={closeModal}
            ></div>
            <div className="relative z-10 w-full max-w-lg sm:max-w-xl border border-gray-200 dark:border-gray-700 shadow-2xl rounded-2xl bg-white dark:bg-gray-900 p-5 sm:p-6 max-h-[90vh] overflow-y-auto">
              <button
                type="button"
                onClick={closeModal}
                className="absolute right-4 top-4 text-gray-400 hover:text-gray-600 focus:outline-none"
                aria-label="Luk"
              >
                <FaTimes className="h-4 w-4" />
              </button>
              <div className="mt-1 sm:mt-3">
                <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
                {editingFejlmelding ? 'Rediger fejlmelding' : 'Tilføj ny fejlmelding'}
              </h3>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                    Beskrivelse
                  </label>
                  <textarea
                    required
                    value={formData.beskrivelse}
                      onChange={(e) => setFormData({...formData, beskrivelse: e.target.value})}
                      className="mt-1 block w-full border border-gray-300 dark:border-gray-700 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-falck-blue focus:border-transparent bg-white dark:bg-gray-800 dark:text-gray-100"
                    rows="3"
                  />
                </div>
                <div className="relative">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                    Bil (internt nummer)
                  </label>
                  <input
                    type="text"
                    value={bilSearchTerm}
                    onChange={handleBilSearchChange}
                    onBlur={handleBilInputBlur}
                    onFocus={() => setShowBilDropdown(bilSearchTerm.length > 0)}
                      className="mt-1 block w-full border border-gray-300 dark:border-gray-700 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-falck-blue focus:border-transparent bg-white dark:bg-gray-800 dark:text-gray-100"
                    placeholder="Søg efter internt nummer eller station..."
                  />
                  {showBilDropdown && filteredBiler.length > 0 && (
                      <div className="absolute z-20 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-md shadow-lg max-h-60 overflow-auto">
                      {filteredBiler.map((bil) => (
                        <div
                          key={bil.id}
                            className="px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer border-b border-gray-100 dark:border-gray-700 last:border-b-0"
                          onClick={() => handleBilSelect(bil)}
                        >
                            <div className="font-medium text-gray-900 dark:text-gray-100">{bil.internt_nummer}</div>
                            <div className="text-sm text-gray-600 dark:text-gray-300">{bil.station_navn || 'Ingen station'}</div>
                        </div>
                      ))}
                    </div>
                  )}
                  {showBilDropdown && filteredBiler.length === 0 && bilSearchTerm.length > 0 && (
                      <div className="absolute z-20 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-md shadow-lg">
                        <div className="px-4 py-2 text-gray-500 dark:text-gray-300 text-sm">
                        Ingen biler fundet. Du kan indtaste manuelt.
                      </div>
                    </div>
                  )}
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                    Kategori
                  </label>
                  <select
                    value={formData.kategori}
                    onChange={(e) => setFormData({...formData, kategori: e.target.value})}
                      className="mt-1 block w-full border border-gray-300 dark:border-gray-700 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-falck-blue focus:border-transparent bg-white dark:bg-gray-800 dark:text-gray-100"
                  >
                    <option value="Fejlmelding">Fejlmelding</option>
                    <option value="Service">Service</option>
                    <option value="Skade">Skade</option>
                    <option value="Opgave">Opgave</option>
                    <option value="Godt at vide">Godt at vide</option>
                  </select>
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                    Status
                  </label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({...formData, status: e.target.value})}
                      className="mt-1 block w-full border border-gray-300 dark:border-gray-700 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-falck-blue focus:border-transparent bg-white dark:bg-gray-800 dark:text-gray-100"
                  >
                    <option value="Åben">Åben</option>
                    <option value="I gang">I gang</option>
                    <option value="Udført">Udført</option>
                  </select>
                </div>
                  <div className="flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-3 space-y-3 sm:space-y-0 pt-4">
                  <button
                    type="button"
                      onClick={closeModal}
                      className="px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-md text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800"
                  >
                    Annuller
                  </button>
                  <button
                    type="submit"
                      className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-falck-blue hover:bg-falck-dark-blue"
                  >
                    {editingFejlmelding ? 'Opdater' : 'Tilføj'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Fejlmeldinger;
